import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { colors, fonts } from "../theme";
import { View, Text, Card, Button, Press, ScreenHeader, Segmented } from "../ui";
import { Icon } from "../Icon";
import { QuestionRunner, ResultSummary } from "../QuestionRunner";
import { useI18n } from "../i18n";
import { useAppState } from "../state";
import { lessonById, t } from "../content";

function Section({ icon, color, title, children }) {
  return (
    <Card style={{ marginBottom: 14 }}>
      <View row style={{ alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Icon name={icon} size={20} color={color} weight="fill" />
        <Text style={{ fontWeight: fonts.bold, fontSize: 15, color: colors.onSurface }}>{title}</Text>
      </View>
      {children}
    </Card>
  );
}
function Body({ children }) {
  return <Text style={{ fontWeight: fonts.regular, fontSize: 15, lineHeight: "24px", color: colors.onSurfaceSecondary }}>{children}</Text>;
}

export default function Lesson() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tr, lang } = useI18n();
  const { state, completeLesson, toggleBookmark } = useAppState();
  const [tab, setTab] = useState("learn");
  const [phase, setPhase] = useState("read");
  const [result, setResult] = useState({ c: 0, t: 0 });
  const lesson = lessonById(id);
  if (!lesson) return null;
  const bookmarked = state.bookmarks.includes(lesson.id);
  const isDone = state.lessons[lesson.id]?.completed;

  if (phase === "quiz") {
    return (
      <QuestionRunner
        title={t(lesson.title, lang)}
        subtitle={tr("miniCheck")}
        questions={lesson.miniCheck}
        onExit={() => setPhase("read")}
        onFinish={(c, tot) => { completeLesson(lesson.id); setResult({ c, t: tot }); setPhase("done"); }}
      />
    );
  }
  if (phase === "done") {
    return (
      <View style={{ flex: 1, minHeight: "100vh", backgroundColor: colors.surface }}>
        <ScreenHeader title={tr("lessonComplete")} onBack={() => navigate(-1)} />
        <ResultSummary correct={result.c} total={result.t} onDone={() => navigate(-1)} />
      </View>
    );
  }

  const bookmarkBtn = (
    <Press testID="lesson-bookmark" onClick={() => toggleBookmark(lesson.id)} style={{
      width: 40, height: 40, borderRadius: 20, backgroundColor: colors.surfaceSecondary, display: "flex",
      alignItems: "center", justifyContent: "center",
    }}>
      <Icon name="bookmark" size={20} color={bookmarked ? colors.brandPrimary : colors.muted} weight={bookmarked ? "fill" : "regular"} />
    </Press>
  );

  return (
    <View style={{ flex: 1, minHeight: "100vh", backgroundColor: colors.surfaceSecondary }}>
      <ScreenHeader title={t(lesson.title, lang)} subtitle={t(lesson.topic, lang)} onBack={() => navigate(-1)} right={bookmarkBtn} />
      <View style={{ padding: "12px 20px" }}>
        <Segmented value={tab} onChange={setTab} items={[
          { key: "learn", label: tr("tLearn") },
          { key: "key", label: tr("tKey") },
          { key: "example", label: tr("tExample") },
        ]} />
      </View>
      <View style={{ padding: "0 20px 120px" }}>
        {tab === "learn" && (
          <>
            <Section icon="target" color={colors.brandPrimary} title={tr("objective")}><Body>{t(lesson.objective, lang)}</Body></Section>
            <Section icon="lightbulb" color={colors.info} title={tr("explained")}><Body>{t(lesson.explanation, lang)}</Body></Section>
            <Section icon="heart" color={colors.success} title={tr("whyMatters")}><Body>{t(lesson.importance, lang)}</Body></Section>
            <Section icon="warning" color={colors.warning} title={tr("commonMistake")}><Body>{t(lesson.commonMistake, lang)}</Body></Section>
            <Section icon="hand" color={colors.brandPrimary} title={tr("applyIt")}><Body>{t(lesson.applyIt, lang)}</Body></Section>
          </>
        )}
        {tab === "key" && (
          <Section icon="check-circle" color={colors.success} title={tr("remember")}>
            <View style={{ gap: 12, marginTop: 4 }}>
              {lesson.keyPoints[lang].map((p, i) => (
                <View key={i} row style={{ gap: 10 }}>
                  <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: colors.brandTertiary, alignItems: "center", justifyContent: "center", marginTop: 1, flexShrink: 0 }}>
                    <Text style={{ fontWeight: fonts.bold, fontSize: 12, color: colors.brandPrimary }}>{i + 1}</Text>
                  </View>
                  <Text style={{ flex: 1, fontWeight: fonts.medium, fontSize: 15, lineHeight: "22px", color: colors.onSurface }}>{p}</Text>
                </View>
              ))}
            </View>
          </Section>
        )}
        {tab === "example" && (
          <Section icon="chats-circle" color={colors.brandPrimary} title={tr("tExample")}><Body>{t(lesson.example, lang)}</Body></Section>
        )}
      </View>
      <View style={{ position: "sticky", bottom: 0, padding: "20px", backgroundColor: colors.surface, borderTop: `1px solid ${colors.divider}` }}>
        <Button testID="lesson-to-quiz" title={isDone ? `${tr("miniCheck")} · ${tr("completed")}` : tr("toQuiz")} icon="cap" onPress={() => setPhase("quiz")} />
      </View>
    </View>
  );
}
